// What the board shows, as plain functions over the payload's rows.
//
// Two layers of filtering, on purpose:
//   - the *scope* (the tab's pipelines, and the rep) drives the stage boxes;
//   - the *view* (scope, then the tab's status and every table filter) drives the table.
// Boxes computed from the view would echo the stage filter back: pick a stage and the others
// would all read zero.
//
// The back end computes every flag once (payload.md). The one threshold derived here is display
// only: a touch 8-13 days old is amber, between green and the back end's stale.
//
// Search stays inside the tab and every filter in use: it only narrows what the table already
// shows. The All tab is how to search everything, since switching to it drops the stage (Vikram).
//
// Whose a row is: its `rep`, and nobody else (GRO-241). Most renewals are owned by an inactive
// user, so the board shows the back end's `rep` wherever it names a person, never the opportunity
// owner (GRO-237). The drawer's Owners block still shows all four owners.

import type { Opportunity, PipelineName, SalesforceDate, Status } from '../api/types.ts';
import { daysBetween } from './format.ts';

export type Tab = 'all' | 'open_new_business' | 'open_expansion' | 'open_renewals' | 'won' | 'lost';
// The tabs a row can live on. All is every row on them, not a home of its own.
export const HOME_TABS: Tab[] = ['open_new_business', 'open_expansion', 'open_renewals', 'won', 'lost'];
export const TABS: Tab[] = ['all', ...HOME_TABS];
export const TAB_LABELS: Record<Tab, string> = {
  all: 'All',
  open_new_business: 'Open New Business',
  open_expansion: 'Open Expansion',
  open_renewals: 'Open Renewals',
  won: 'Closed won',
  lost: 'Closed lost',
};

// The tag a row carries on All, or in a search, naming the tab it lives on.
export const HOME_TAB_TAGS: Record<Tab, string> = {
  all: 'All',
  open_new_business: 'New business',
  open_expansion: 'Expansion',
  open_renewals: 'Renewal',
  won: 'Closed won',
  lost: 'Closed lost',
};

// Tab keys an older link may still carry, and the tab each one opens now. Open Enterprise was
// split into New Business and Expansion (GRO-241); its links open New Business, the default.
export const LEGACY_TABS = new Map<string, Tab>([
  ['open_enterprise', 'open_new_business'],
]);

export type Pipeline = PipelineName;

// New Business and Expansion together: what the closed tabs hold, and every pipeline that shows
// all five stage boxes.
export const ENTERPRISE_PIPELINES: Pipeline[] = ['new_business', 'expansion'];

export type Reason = 'stale' | 'no_rep_next_step' | 'past_close';
export const REASONS: Reason[] = ['stale', 'no_rep_next_step', 'past_close'];
export const REASON_LABELS: Record<Reason, string> = {
  stale: 'Stale',
  no_rep_next_step: 'No next step',
  past_close: 'Past close',
};

// A period is a month (`2026-09`) or a calendar quarter (`2026-Q3`).
export type Period = string;

export interface Filters {
  tab: Tab;
  // The person picked in the rep picker; see matchesPerson.
  rep_id: string | null;
  stage: number | null;
  reasons: Reason[];
  // Free text matched against the account and opportunity names.
  search: string;
  created: Period | null;
  closing: Period | null;
}

export const DEFAULT_FILTERS: Filters = {
  tab: 'open_new_business',
  rep_id: null,
  stage: null,
  reasons: [],
  search: '',
  created: null,
  closing: null,
};

export type SortKey =
  | 'account' | 'opportunity' | 'rep' | 'stage' | 'pipeline' | 'close' | 'last_touch' | 'next_step';

// `null` is the default order: by close date (see compareByDefault).
export interface Sort {
  key: SortKey;
  direction: 'asc' | 'desc';
}

// --- Tabs and scope --------------------------------------------------------------------------

// All holds every status, so it has none of its own.
export function tabStatus(tab: Tab): Status | null {
  if (tab === 'all') return null;
  if (tab === 'won') return 'won';
  if (tab === 'lost') return 'lost';
  return 'open';
}

// Each open tab is one pipeline. The closed tabs hold New Business and Expansion together:
// renewals are their own tab only while open (Vikram, GRO-217).
export function tabPipelines(tab: Tab): Pipeline[] {
  if (tab === 'all') return [...ENTERPRISE_PIPELINES, 'renewal'];
  if (tab === 'open_new_business') return ['new_business'];
  if (tab === 'open_expansion') return ['expansion'];
  if (tab === 'open_renewals') return ['renewal'];
  return ENTERPRISE_PIPELINES;
}

// The open tabs and All have stage boxes, a stage filter and the attention reasons. On All the
// boxes count every open deal, renewals included, and a picked stage or reason keeps only open
// rows: a closed row has no stage number and no attention flags (Vikram, for consistency).
export function tabHasStages(tab: Tab): boolean {
  return tab === 'all' || tabStatus(tab) === 'open';
}

// Where a stage box click lands: the open tab or All itself, or New Business from a closed tab.
export function openTabFor(tab: Tab): Tab {
  return tabHasStages(tab) ? tab : 'open_new_business';
}

// The back end decides the pipeline (payload.md): renewal by record type or type, else new
// business or expansion by type, and an untyped deal by whether its account has won before.
export function inPipelines(opportunity: Opportunity, pipelines: Pipeline[]): boolean {
  return pipelines.includes(opportunity.pipeline);
}

// The tab a row lives on, by its pipeline and status. A closed renewal has none: the closed tabs
// hold New Business and Expansion only (GRO-217), so no tab, All included, shows it.
export function homeTab(opportunity: Opportunity): Tab | null {
  for (const tab of HOME_TABS) {
    if (inPipelines(opportunity, tabPipelines(tab)) && opportunity.status === tabStatus(tab)) return tab;
  }
  return null;
}

// All holds every row that has a home tab.
function inTab(opportunity: Opportunity, tab: Tab): boolean {
  const home = homeTab(opportunity);
  return home !== null && (tab === 'all' || home === tab);
}

// A new tab keeps the rep, search and dates but drops the stage: a stage picked on one pipeline
// means a different box, or none, on the other.
export function switchTab(filters: Filters, tab: Tab): Filters {
  return { ...filters, tab, stage: null };
}

// --- Whose a row is --------------------------------------------------------------------------

// A row is someone's only when they are its rep. Its CS owner or opportunity owner doesn't make it
// theirs: a deal with a rep shows under that rep alone (Vikram, GRO-241).
export function matchesPerson(opportunity: Opportunity, userId: string): boolean {
  return opportunity.rep.user_id === userId;
}

function matchesRep(opportunity: Opportunity, repId: string | null): boolean {
  return repId === null || matchesPerson(opportunity, repId);
}

// Anyone the payload names, so an empty tab can say whose rows it has none of.
export function personName(opportunities: Opportunity[], userId: string): string | null {
  for (const opportunity of opportunities) {
    const people = [opportunity.rep, opportunity.cs_owner, opportunity.account_owner, opportunity.sdr_owner, opportunity.owner];
    const match = people.find(person => person?.user_id === userId);
    if (match) return match.name;
  }
  return null;
}

export function scopeRows(opportunities: Opportunity[], filters: Filters): Opportunity[] {
  const pipelines = tabPipelines(filters.tab);
  return opportunities.filter(opportunity => (
    inPipelines(opportunity, pipelines) && matchesRep(opportunity, filters.rep_id)
  ));
}

// --- Stages ----------------------------------------------------------------------------------

// The back end numbers the five Enterprise stage names only. Any other name that starts with a
// stage number counts as that stage: Customer Adoption on Renewals and the older Discovery are
// both "1. ...". The row's pill keeps the real name.
export function funnelStageNumber(opportunity: Opportunity): number | null {
  if (opportunity.stage_number !== null) return opportunity.stage_number;
  const leading = /^([1-5])\.\s/.exec(opportunity.stage);
  return leading ? Number(leading[1]) : null;
}

// --- Periods ---------------------------------------------------------------------------------

function monthOf(date: SalesforceDate): Period {
  return date.slice(0, 7);
}

function quarterOf(date: SalesforceDate): Period {
  const quarter = Math.ceil(Number(date.slice(5, 7)) / 3);
  return `${date.slice(0, 4)}-Q${quarter}`;
}

export function inPeriod(date: SalesforceDate, period: Period): boolean {
  return period.includes('Q') ? quarterOf(date) === period : monthOf(date) === period;
}

const MONTH_NAME = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });

// "Sep 2026" or "Q3 2026".
export function periodLabel(period: Period): string {
  const [year, part = ''] = period.split('-');
  if (part.startsWith('Q')) return `${part} ${year}`;
  return MONTH_NAME.format(new Date(`${period}-01T00:00:00Z`));
}

export interface PeriodOptions {
  quarters: Period[];
  months: Period[];
}

// The months and quarters the rows actually fall in, oldest first.
export function periodOptions(rows: Opportunity[], dateOf: (opportunity: Opportunity) => SalesforceDate): PeriodOptions {
  const months = new Set<Period>();
  const quarters = new Set<Period>();
  for (const opportunity of rows) {
    const date = dateOf(opportunity);
    months.add(monthOf(date));
    quarters.add(quarterOf(date));
  }
  return { quarters: [...quarters].sort(), months: [...months].sort() };
}

export function createdDate(opportunity: Opportunity): SalesforceDate {
  return opportunity.created_date;
}

export function closeDate(opportunity: Opportunity): SalesforceDate {
  return opportunity.close_date;
}

// --- The All tab's tags ---------------------------------------------------------------------

// On All, each row carries a tag naming the tab it lives on, so the mix of tabs is readable.
export function showsHomeTab(tab: Tab): boolean {
  return tab === 'all';
}

// --- The table's rows ------------------------------------------------------------------------

// Several reasons picked means "any of them", so two widen the view rather than intersecting it.
function matchesReasons(opportunity: Opportunity, reasons: Reason[]): boolean {
  if (reasons.length === 0) return true;
  return reasons.some(reason => opportunity.attention[reason]);
}

// Case- and accent-insensitive, so "acme" finds "ACME Inc" and "cafe" finds "Café".
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function matchesSearch(opportunity: Opportunity, search: string): boolean {
  const needle = normalize(search.trim());
  if (!needle) return true;
  return normalize(`${opportunity.account_name ?? ''} ${opportunity.opp_name}`).includes(needle);
}

// Stage and the attention reasons only mean something on an open tab or All; carried over to a
// closed one they would empty it with their controls hidden, so they are ignored there.
export function viewRows(opportunities: Opportunity[], filters: Filters): Opportunity[] {
  const tab = filters.tab;
  const stagesApply = tabHasStages(tab);
  return opportunities.filter(opportunity => {
    if (!inTab(opportunity, tab)) return false;
    if (!matchesRep(opportunity, filters.rep_id)) return false;
    if (stagesApply && filters.stage !== null && funnelStageNumber(opportunity) !== filters.stage) return false;
    if (stagesApply && !matchesReasons(opportunity, filters.reasons)) return false;
    if (!matchesSearch(opportunity, filters.search)) return false;
    if (filters.created && !inPeriod(opportunity.created_date, filters.created)) return false;
    if (filters.closing && !inPeriod(opportunity.close_date, filters.closing)) return false;
    return true;
  });
}

// Every tab counts the rows for the rep, so a rep's tabs add up to what they own, and All is their sum.
export function tabCounts(opportunities: Opportunity[], filters: Filters): Record<Tab, number> {
  const counts: Record<Tab, number> = { all: 0, open_new_business: 0, open_expansion: 0, open_renewals: 0, won: 0, lost: 0 };
  for (const opportunity of opportunities) {
    if (!matchesRep(opportunity, filters.rep_id)) continue;
    for (const tab of TABS) if (inTab(opportunity, tab)) counts[tab] += 1;
  }
  return counts;
}

// The table filters in use, not counting the tab or the rep.
export function activeFilterCount(filters: Filters): number {
  const open = tabHasStages(filters.tab);
  let count = 0;
  if (open && filters.stage !== null) count += 1;
  if (open) count += filters.reasons.length;
  if (filters.created) count += 1;
  if (filters.closing) count += 1;
  return count;
}

export function clearTableFilters(filters: Filters): Filters {
  return { ...DEFAULT_FILTERS, tab: filters.tab, rep_id: filters.rep_id };
}

// What each tab holds, for the empty table's sentence.
const TAB_NOUNS: Record<Tab, string> = {
  all: 'opportunities',
  open_new_business: 'open New Business opportunities',
  open_expansion: 'open Expansion opportunities',
  open_renewals: 'open renewals',
  won: 'closed-won opportunities',
  lost: 'closed-lost opportunities',
};

// "No open renewals for Avery Quill.", or without the name when nobody is picked.
export function emptyTabMessage(tab: Tab, repName: string | null): string {
  return repName ? `No ${TAB_NOUNS[tab]} for ${repName}.` : `No ${TAB_NOUNS[tab]}.`;
}

// --- The rep list ----------------------------------------------------------------------------

export interface RepOption {
  user_id: string;
  name: string;
  row_count: number;
}

// Every rep with a row in the current view, by name, each counted as the picker would filter. The
// view is the tab plus every other filter in use (stage, reasons, dates, search), so the list
// narrows with them: on Expansion with stage 2 picked, only the reps who have a stage-2 expansion
// are offered (Vikram, 28 Sep). The rep filter itself is left out, or the list would be one name.
export function repOptions(opportunities: Opportunity[], filters: Filters): RepOption[] {
  const byId = new Map<string, RepOption>();
  for (const opportunity of viewRows(opportunities, { ...filters, rep_id: null })) {
    const rep = opportunity.rep;
    const existing = byId.get(rep.user_id);
    if (existing) existing.row_count += 1;
    else byId.set(rep.user_id, { user_id: rep.user_id, name: rep.name, row_count: 1 });
  }
  return [...byId.values()].sort(compareRepOptions);
}

function compareRepOptions(left: RepOption, right: RepOption): number {
  return left.name.localeCompare(right.name);
}

// --- Stage boxes -----------------------------------------------------------------------------

export interface FunnelStage {
  label: string;
  stage_number: number | null;
  count: number;
  // The sum of the rows' pipeline_value.
  pipeline_value: number;
  stale_count: number;
  no_rep_next_step_count: number;
  past_close_count: number;
}

export interface Funnel {
  stages: FunnelStage[];
  // Any stage name outside 1-5, one line each, largest first.
  others: FunnelStage[];
}

function emptyStage(label: string, stageNumber: number | null): FunnelStage {
  return {
    label, stage_number: stageNumber, count: 0, pipeline_value: 0,
    stale_count: 0, no_rep_next_step_count: 0, past_close_count: 0,
  };
}

function addToStage(stage: FunnelStage, opportunity: Opportunity): void {
  stage.count += 1;
  stage.pipeline_value += opportunity.pipeline_value ?? 0;
  if (opportunity.attention.stale) stage.stale_count += 1;
  if (opportunity.attention.no_rep_next_step) stage.no_rep_next_step_count += 1;
  if (opportunity.attention.past_close) stage.past_close_count += 1;
}

// The name most rows in a stage carry, so the label doesn't depend on which row came first.
function mostCommonName(nameCounts: Map<string, number>): string | null {
  let best: string | null = null;
  let bestCount = 0;
  for (const [name, count] of nameCounts) {
    if (count > bestCount) {
      best = name;
      bestCount = count;
    }
  }
  return best;
}

// Salesforce's Enterprise stage names (New Business and Expansion share them), for a box no open
// row in the pipeline is in.
const STANDARD_STAGE_NAMES = [
  '1. Qualification', '2. Use Case Identification', '3. Technical Evaluation', '4. Business Decision', '5. Procurement',
];

// How many open rows in `labelRows` carry each name, per stage 1-5. Taken from the whole
// pipeline, not the rep's rows, so a rep with nothing in a stage still sees its box and name.
function stageNameCounts(labelRows: Opportunity[]): Array<Map<string, number>> {
  const stageNames = STANDARD_STAGE_NAMES.map(() => new Map<string, number>());
  for (const opportunity of labelRows) {
    if (opportunity.status !== 'open') continue;
    const stageNumber = funnelStageNumber(opportunity);
    const names = stageNumber ? stageNames[stageNumber - 1] : undefined;
    if (names) names.set(opportunity.stage, (names.get(opportunity.stage) ?? 0) + 1);
  }
  return stageNames;
}

// Open rows only. On New Business and Expansion, stages 1-5 always appear, empty or not, so the
// boxes keep their places as filters change. Renewals use other stage names and nearly all sit in
// stage 1, so their boxes are only the stages some open renewal is in, in stage order, each named
// from its rows. `pipelines` are the tab's (tabPipelines).
export function buildFunnel(
  scoped: Opportunity[], labelRows: Opportunity[] = scoped, pipelines: Pipeline[] = ENTERPRISE_PIPELINES,
): Funnel {
  // Each stage's label is the name most open rows in it carry, else Salesforce's standard name.
  const nameCounts = stageNameCounts(labelRows);
  const allStages = STANDARD_STAGE_NAMES.map((standard, index) => (
    emptyStage(mostCommonName(nameCounts[index] ?? new Map()) ?? standard, index + 1)
  ));
  const others = new Map<string, FunnelStage>();

  for (const opportunity of scoped) {
    if (opportunity.status !== 'open') continue;

    // A numbered stage.
    const stageNumber = funnelStageNumber(opportunity);
    const numbered = stageNumber ? allStages[stageNumber - 1] : undefined;
    if (numbered) {
      addToStage(numbered, opportunity);
      continue;
    }

    // Any other stage name, one line per name.
    let other = others.get(opportunity.stage);
    if (!other) {
      other = emptyStage(opportunity.stage, null);
      others.set(opportunity.stage, other);
    }
    addToStage(other, opportunity);
  }

  return { stages: shownStages(allStages, nameCounts, pipelines), others: [...others.values()].sort(compareByCount) };
}

// Every box on New Business and Expansion; on renewals, only the stages the pipeline's open rows are in.
function shownStages(stages: FunnelStage[], nameCounts: Array<Map<string, number>>, pipelines: Pipeline[]): FunnelStage[] {
  // Renewals alone keep only the stages in use; any tab with New Business or Expansion shows all five.
  if (pipelines.some(pipeline => pipeline !== 'renewal')) return stages;
  return stages.filter((_stage, index) => (nameCounts[index]?.size ?? 0) > 0);
}

function compareByCount(left: FunnelStage, right: FunnelStage): number {
  return right.count - left.count;
}

// --- How recent the last touch is ------------------------------------------------------------

export type Level = 'good' | 'warn' | 'bad';

const WARN_AFTER_DAYS = 8;

// Green: the rep touched it in the last 7 days (GRO-245). Amber: 8 days up to stale, or touched only by
// someone other than the rep. Red: stale, or never touched. Closed rows carry no colour.
export function touchLevel(opportunity: Opportunity, today: SalesforceDate): Level | null {
  if (opportunity.status !== 'open') return null;
  const touch = opportunity.last_touch;
  if (!touch || opportunity.attention.stale) return 'bad';
  if (daysBetween(touch.date, today) >= WARN_AFTER_DAYS) return 'warn';
  if (!touch.by_owner || opportunity.attention.touched_only_by_others) return 'warn';
  return 'good';
}

// --- Sorting ---------------------------------------------------------------------------------

function compareNumbers(left: number, right: number): number {
  return left - right;
}

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

// Stages by number, with a non-standard stage after 5; within a stage, longer in it sorts later.
function stageRank(opportunity: Opportunity): number {
  return (funnelStageNumber(opportunity) ?? 6) * 100000 + opportunity.days_in_stage;
}

// A missing rep next step sorts first when ascending: the gap is the thing being looked for.
function nextStepText(opportunity: Opportunity): string {
  return opportunity.next_step.source === 'rep' ? opportunity.next_step.text ?? '' : '';
}

function sortValue(opportunity: Opportunity, key: SortKey): string | number | null {
  switch (key) {
    case 'account': return opportunity.account_name ?? '';
    case 'opportunity': return opportunity.opp_name;
    case 'rep': return opportunity.rep.name;
    case 'stage': return stageRank(opportunity);
    // No pipeline value (null or $0) isn't the smallest; it sorts last either way.
    case 'pipeline': return opportunity.pipeline_value || null;
    case 'close': return opportunity.close_date;
    // Never touched is the oldest touch of all, so it leads an ascending sort.
    case 'last_touch': return opportunity.last_touch?.date ?? '0000-00-00';
    case 'next_step': return nextStepText(opportunity);
  }
}

function compareValues(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return compareNumbers(left, right);
  return compareText(String(left), String(right));
}

function compareBySort(left: Opportunity, right: Opportunity, sort: Sort): number {
  const leftValue = sortValue(left, sort.key);
  const rightValue = sortValue(right, sort.key);

  // A missing pipeline value goes last whichever way the column runs.
  if (leftValue === null && rightValue === null) return compareByDefault(left, right);
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;

  const compared = compareValues(leftValue, rightValue);
  const directed = sort.direction === 'asc' ? compared : -compared;
  return directed || compareByDefault(left, right);
}

// By close date: soonest first on the open tabs, so what is past close or closing next leads;
// most recent first on the closed tabs. Ties go attention first, then the larger pipeline value, then
// the account name, so the order is stable. All and a search mix statuses, so open rows lead
// there and each group keeps its own direction; on one tab this changes nothing.
function compareByDefault(left: Opportunity, right: Opportunity): number {
  const byOpen = Number(right.status === 'open') - Number(left.status === 'open');
  if (byOpen) return byOpen;
  const byClose = compareText(left.close_date, right.close_date);
  if (byClose) return left.status === 'open' ? byClose : -byClose;
  const attention = Number(right.needs_attention) - Number(left.needs_attention);
  if (attention) return attention;
  const value = (right.pipeline_value ?? -1) - (left.pipeline_value ?? -1);
  if (value) return value;
  return compareText(left.account_name ?? '', right.account_name ?? '');
}

export function sortRows(rows: Opportunity[], sort: Sort | null): Opportunity[] {
  const sorted = [...rows];
  if (sort) sorted.sort((left, right) => compareBySort(left, right, sort));
  else sorted.sort(compareByDefault);
  return sorted;
}

// A header click: a new column starts in its natural direction, the same column flips, and a
// third click returns to the default order.
const NATURAL_DIRECTION: Record<SortKey, 'asc' | 'desc'> = {
  account: 'asc', opportunity: 'asc', rep: 'asc', stage: 'asc',
  pipeline: 'desc', close: 'asc', last_touch: 'asc', next_step: 'asc',
};

export function nextSort(current: Sort | null, key: SortKey): Sort | null {
  const natural = NATURAL_DIRECTION[key];
  if (!current || current.key !== key) return { key, direction: natural };
  if (current.direction === natural) return { key, direction: natural === 'asc' ? 'desc' : 'asc' };
  return null;
}

export const SORT_KEYS = Object.keys(NATURAL_DIRECTION) as SortKey[];
